import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box, Button, Dialog, DialogActions, DialogContent, DialogTitle,
  IconButton, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TextField, Typography, Checkbox, FormControlLabel, Alert, Chip
} from '@mui/material';
import { Add, Delete, Edit } from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { getApartmentUsers, createAndAddUser, updateApartmentUser, removeUserFromApartment, USE_MOCK_DATA } from '../services/api';
import { mockApartmentUsers } from '../services/mockData';

export default function ApartmentUsers() {
  const { id: apartmentId } = useParams();
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [editForm, setEditForm] = useState({ isOwner: false, isAdmin: false });
  const [error, setError] = useState('');
  const [editError, setEditError] = useState('');
  const [form, setForm] = useState({
    fullName: '',
    email: '',
    phone: '',
    password: '',
    flatNumber: '',
    isOwner: false,
    isAdmin: false,
    openingBalance: ''
  });
  const { isAdmin } = useAuth();

  const loadData = () => {
    if (USE_MOCK_DATA) {
      setUsers(mockApartmentUsers);
      return;
    }
    getApartmentUsers(apartmentId).then(res => setUsers(res.data)).catch(() => {});
  };

  useEffect(() => { loadData(); }, [apartmentId]);

  const handleSubmit = async () => {
    setError('');
    if (!form.fullName || !form.email || !form.phone || !form.password || !form.flatNumber) {
      setError('Please fill all required fields');
      return;
    }
    if (form.phone.length !== 10 || !/^\d{10}$/.test(form.phone)) {
      setError('Phone number must be exactly 10 digits');
      return;
    }
    if (USE_MOCK_DATA) {
      setUsers(prev => [...prev, {
        id: Date.now(), apartmentId: parseInt(apartmentId), apartmentName: 'Sunshine Residency',
        userId: Date.now(), userName: form.fullName, email: form.email, phone: form.phone,
        flatNumber: form.flatNumber, isOwner: form.isOwner, isAdmin: form.isAdmin, joinedAt: new Date().toISOString()
      }]);
      setOpen(false);
      setForm({ fullName: '', email: '', phone: '', password: '', flatNumber: '', isOwner: false, isAdmin: false, openingBalance: '' });
      return;
    }
    try {
      await createAndAddUser(apartmentId, {
        fullName: form.fullName,
        email: form.email,
        phone: form.phone,
        password: form.password,
        flatNumber: form.flatNumber,
        isOwner: form.isOwner,
        isAdmin: form.isAdmin,
        openingBalance: form.openingBalance ? parseFloat(form.openingBalance) : 0
      });
      setOpen(false);
      setForm({ fullName: '', email: '', phone: '', password: '', flatNumber: '', isOwner: false, isAdmin: false, openingBalance: '' });
      loadData();
    } catch (err) {
      setError(err.response?.data || 'Failed to add member');
    }
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setEditForm({ isOwner: user.isOwner, isAdmin: user.isAdmin, openingBalance: (user.openingBalance || 0).toString() });
    setEditError('');
    setEditOpen(true);
  };

  const handleEditSubmit = async () => {
    setEditError('');
    if (USE_MOCK_DATA) {
      setUsers(prev => prev.map(u => u.id === editingUser.id
        ? { ...u, isOwner: editForm.isOwner, isAdmin: editForm.isAdmin, openingBalance: parseFloat(editForm.openingBalance) || 0 }
        : u
      ));
      setEditOpen(false);
      setEditingUser(null);
      return;
    }
    try {
      await updateApartmentUser(apartmentId, editingUser.id, {
        isOwner: editForm.isOwner,
        isAdmin: editForm.isAdmin,
        openingBalance: parseFloat(editForm.openingBalance) || 0
      });
      setEditOpen(false);
      setEditingUser(null);
      loadData();
    } catch (err) {
      setEditError(err.response?.data || 'Failed to update user');
    }
  };

  const handleRemove = async (id) => {
    if (window.confirm('Remove this user from apartment?')) {
      if (USE_MOCK_DATA) {
        setUsers(prev => prev.filter(u => u.id !== id));
      } else {
        await removeUserFromApartment(apartmentId, id);
        loadData();
      }
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h5">Apartment Members</Typography>
        {isAdmin() && (
          <Button variant="contained" startIcon={<Add />} onClick={() => { setError(''); setOpen(true); }}>
            Add Member
          </Button>
        )}
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Phone</TableCell>
              <TableCell>Flat Number</TableCell>
              <TableCell>Role</TableCell>
              <TableCell>Owner</TableCell>
              <TableCell align="right">Opening Balance</TableCell>
              <TableCell>Joined</TableCell>
              {isAdmin() && <TableCell>Actions</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {users.map(u => (
              <TableRow key={u.id}>
                <TableCell>{u.userName}</TableCell>
                <TableCell>{u.email}</TableCell>
                <TableCell>{u.phone}</TableCell>
                <TableCell>{u.flatNumber}</TableCell>
                <TableCell>
                  <Chip label={u.isAdmin ? 'Admin' : 'Normal'} size="small" color={u.isAdmin ? 'warning' : 'default'} />
                </TableCell>
                <TableCell>{u.isOwner ? 'Yes' : 'No'}</TableCell>
                <TableCell align="right">₹{(u.openingBalance || 0).toFixed(2)}</TableCell>
                <TableCell>{new Date(u.joinedAt).toLocaleDateString()}</TableCell>
                {isAdmin() && (
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    <IconButton onClick={() => handleEdit(u)} color="primary"><Edit /></IconButton>
                    <IconButton onClick={() => handleRemove(u.id)} color="error"><Delete /></IconButton>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add Member Dialog */}
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Member to Apartment</DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            This will create a login account for the user. They can sign in with their email/phone and password.
          </Typography>
          <TextField fullWidth label="Full Name" margin="dense" required
            value={form.fullName} onChange={(e) => setForm({ ...form, fullName: e.target.value })} />
          <TextField fullWidth label="Email" type="email" margin="dense" required
            value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          <TextField fullWidth label="Phone Number" type="tel" margin="dense" required
            value={form.phone} onChange={(e) => {
              const val = e.target.value.replace(/\D/g, '').slice(0, 10);
              setForm({ ...form, phone: val });
            }}
            inputProps={{ maxLength: 10, pattern: '[0-9]{10}' }}
            helperText={form.phone && form.phone.length !== 10 ? 'Phone number must be exactly 10 digits' : ''}
            error={form.phone.length > 0 && form.phone.length !== 10} />
          <TextField fullWidth label="Password" type="password" margin="dense" required
            value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
            helperText="Share this password with the user so they can login" />
          <TextField fullWidth label="Flat Number" margin="dense" required
            value={form.flatNumber} onChange={(e) => setForm({ ...form, flatNumber: e.target.value })}
            placeholder="e.g. A-101" />
          <FormControlLabel
            control={<Checkbox checked={form.isOwner} onChange={(e) => setForm({ ...form, isOwner: e.target.checked })} />}
            label="Is Owner"
          />
          <FormControlLabel
            control={<Checkbox checked={form.isAdmin} onChange={(e) => setForm({ ...form, isAdmin: e.target.checked })} />}
            label="Make Admin (can manage apartment expenses & users)"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">Create & Add Member</Button>
        </DialogActions>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit User - {editingUser?.userName}</DialogTitle>
        <DialogContent>
          {editError && <Alert severity="error" sx={{ mb: 1 }}>{editError}</Alert>}
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, mt: 1 }}>
            Update role and ownership for <strong>{editingUser?.userName}</strong> (Flat: {editingUser?.flatNumber})
          </Typography>
          <TextField fullWidth label="Opening Balance (₹)" type="number" margin="dense"
            value={editForm.openingBalance} onChange={(e) => setEditForm({ ...editForm, openingBalance: e.target.value })}
            helperText="Previous outstanding amount from old records" />
          <FormControlLabel
            control={<Checkbox checked={editForm.isAdmin} onChange={(e) => setEditForm({ ...editForm, isAdmin: e.target.checked })} />}
            label="Admin (can manage apartment expenses & users)"
          />
          <FormControlLabel
            control={<Checkbox checked={editForm.isOwner} onChange={(e) => setEditForm({ ...editForm, isOwner: e.target.checked })} />}
            label="Owner"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)}>Cancel</Button>
          <Button onClick={handleEditSubmit} variant="contained">Save Changes</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
